import {tokens} from '@pleo-io/telescope'
import {useRect} from '@reach/rect'
import React from 'react'
import styled from 'styled-components'

import Image from '@helios/shared/components/image'
import PrismicLink, {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import {Text} from '@helios/shared/styles/typography'
import {radii} from '@helios/shared/theme'
import ImageSource from '@helios/shared/types/image'

interface Props {
    ctaLink?: PrismicLinkProps
    ctaText?: string
    image: ImageSource
    mirrored?: boolean
}

const CircularImageWithCta = ({ctaLink, ctaText, image, mirrored = false}: Props) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null)
    const refDimensions = useRect(containerRef)
    // Using a ref and dynamically calculating container width as SVG dimensions can't be styled with CSS
    const containerSize = refDimensions?.width ?? 0
    const radius = containerSize / 2
    // Padding is set at a fixed 80px which is the distance between the edge of circle and outside the text
    const padding = 80

    return (
        <Container ref={containerRef}>
            <ImageContainer $containerSize={containerSize} $padding={padding}>
                {image && <Image image={image} width={600} />}
            </ImageContainer>
            {/* 
                To draw curved text around the image, we create an SVG circle which is larger
                than the image by a fixed 80px which is set in the ImageContainer styles below. We then 
                add textPath linked to the SVG circle by #circle
            */}
            <StyledSVG
                $containerSize={containerSize}
                viewBox={`0 0 ${containerSize} ${containerSize}`}
            >
                <path
                    id="curve"
                    fill="none"
                    d={`M0 ${radius} A${radius} ${radius} 0 1 0 ${radius} 0`}
                />
                {ctaLink && ctaText && (
                    <StyledPrismicLink {...ctaLink}>
                        <Text as="text" variant="paragraph-large">
                            {/* 
                                startOffset is the % around the curve the text starts, with the starting
                                point being 0 on the X-axis, continuing anti-clockwise
                            */}
                            <textPath xlinkHref="#curve" startOffset={mirrored ? '45%' : '18%'}>
                                {ctaText} →
                            </textPath>
                        </Text>
                    </StyledPrismicLink>
                )}
            </StyledSVG>
        </Container>
    )
}

export default CircularImageWithCta

const Container = styled.div`
    position: relative;
    display: grid;
    width: 100%;
    height: 100%;
    justify-items: center;
    align-items: center;
`

interface ImageContainerProps {
    $containerSize: number
    $padding: number
}

const ImageContainer = styled.div<ImageContainerProps>`
    width: ${(props) => `${props.$containerSize - props.$padding}px`};
    height: ${(props) => `${props.$containerSize - props.$padding}px`};
    overflow: hidden;
    border-radius: ${radii.circle};
    aspect-ratio: 1;
`

interface StyledSVGProps {
    $containerSize: number
}

const StyledSVG = styled.svg<StyledSVGProps>`
    position: absolute;
    width: ${(props) => `${props.$containerSize}px`};
    height: ${(props) => `${props.$containerSize}px`};
`

const StyledPrismicLink = styled(PrismicLink)`
    :hover,
    :focus,
    :active {
        text {
            fill: ${tokens.pink700};
            transition: fill ${tokens.fastIn};
        }
    }
`
